import type { InvitationData } from '../types';
import { PLOT_SCENES, assetUrl } from './cameraPath';

/**
 * Host photo slots for the six planes.
 *
 * Precedence, first hit wins:
 * 1. `customData.plotPlane.photos[id]` (also `wedding.plotPlane.photos`,
 *    `expMeta.plotPlanePhotos`, `customData.plotPlanePhotos`)
 * 2. `weddingEvents[].coverImageUrl` when the event title matches the beat.
 *    A plain Mehndi / Mehendi cover fills the wide plate. A title with
 *    “hands”, “close-up”, or “thumb” fills the hands plate, even when it
 *    is the only cover. A second plain Mehndi cover also fills hands.
 * 3. `mediaUrls[index]` in scene order (0 sangeet … 5 saat pheras) when that
 *    beat still has no keyed or cover URL.
 * 4. The bundled Aisha & Kabir plate.
 *
 * Remote URLs must be http(s). Same-origin paths must start with a single `/`.
 * `javascript:`, `data:`, and `blob:` are rejected.
 *
 * Demo override: `/demo/wedding-plot-plane?sangeet=<url>&mehndi=<url>&hands=<url>&ring=<url>&havan=<url>&pheras=<url>`
 */
export const PLANE_SLOT_IDS = ['sangeet', 'mehndi', 'hands', 'ring', 'havan', 'pheras'] as const;

export type PlaneSlotId = (typeof PLANE_SLOT_IDS)[number];

export interface PlaneSource {
  id: PlaneSlotId;
  src: string;
  fallbackSrc: string;
  fromHost: boolean;
}

const SCENE_TITLE: Record<PlaneSlotId, RegExp> = {
  sangeet: /\bsangeet\b/i,
  mehndi: /\bmehendi\b|\bmehndi\b|\bhenna\b/i,
  hands: /\bmehendi\b|\bmehndi\b|\bhenna\b/i,
  ring: /\bring\b/i,
  havan: /\bhavan\b|\bagni\b|\bhomam\b/i,
  pheras: /\bsaat\b|\bpheras?\b|\bmandap\b/i,
};

const HANDS_TITLE = /\bhands\b|\bclose-?up\b|\bthumb\b/i;

interface LooseEvent {
  title?: string;
  coverImageUrl?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function hostPlaneUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 2048) return null;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.includes('\\') && !trimmed.includes('..')) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (url.username || url.password) return null;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function photoMaps(data: InvitationData): Array<Record<string, unknown> | null> {
  const custom = asRecord(data.customData);
  const wedding = asRecord(custom?.wedding);
  const expMeta = asRecord(custom?.expMeta) ?? asRecord(wedding?.expMeta);
  const fromPlot = (record: Record<string, unknown> | null) => asRecord(asRecord(record?.plotPlane)?.photos);
  return [
    fromPlot(custom),
    fromPlot(wedding),
    asRecord(expMeta?.plotPlanePhotos),
    asRecord(custom?.plotPlanePhotos),
    asRecord(wedding?.plotPlanePhotos),
  ];
}

function explicitUrl(maps: Array<Record<string, unknown> | null>, id: PlaneSlotId): string | null {
  for (const map of maps) {
    const url = hostPlaneUrl(map?.[id]);
    if (url) return url;
  }
  return null;
}

function readEvents(data: InvitationData): LooseEvent[] {
  const custom = asRecord(data.customData);
  const wedding = asRecord(custom?.wedding);
  const raw = wedding?.weddingEvents ?? wedding?.ceremonies ?? custom?.ceremonies;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => item && typeof item === 'object') as LooseEvent[];
}

/** Same title rules the photo covers use. Hands-only titles never match the wide Mehndi beat. */
export function titleMatchesBeat(id: PlaneSlotId, title: string): boolean {
  if (id === 'hands') return HANDS_TITLE.test(title) || SCENE_TITLE.hands.test(title);
  if (!SCENE_TITLE[id].test(title)) return false;
  if (id === 'mehndi' && HANDS_TITLE.test(title)) return false;
  return true;
}

/**
 * Assign each beat at most one event, in plane order. A plain Mehndi title
 * fills the wide beat. A hands / close-up / thumb title fills the hands beat.
 * A second plain Mehndi title also fills hands. Vague titles such as
 * "Evening Gathering" match nothing.
 */
export function assignBeatEvents<T extends { title?: string }>(events: T[]): Map<PlaneSlotId, T> {
  const claimed = new Set<number>();
  const assigned = new Map<PlaneSlotId, T>();

  const take = (id: PlaneSlotId): T | null => {
    const index = events.findIndex((event, i) => !claimed.has(i) && titleMatchesBeat(id, event.title ?? ''));
    if (index < 0) return null;
    claimed.add(index);
    return events[index] ?? null;
  };

  for (const id of PLANE_SLOT_IDS) {
    if (id === 'hands') continue;
    const event = take(id);
    if (event) assigned.set(id, event);
  }
  const hands = take('hands');
  if (hands) assigned.set('hands', hands);
  return assigned;
}

function claimCovers(events: LooseEvent[]): Map<PlaneSlotId, string> {
  const claimed = new Set<number>();
  const covers = new Map<PlaneSlotId, string>();

  const take = (id: PlaneSlotId): string | null => {
    const index = events.findIndex((event, i) => {
      if (claimed.has(i)) return false;
      if (!titleMatchesBeat(id, event.title ?? '')) return false;
      return hostPlaneUrl(event.coverImageUrl) !== null;
    });
    if (index < 0) return null;
    claimed.add(index);
    return hostPlaneUrl(events[index]?.coverImageUrl);
  };

  for (const id of PLANE_SLOT_IDS) {
    if (id === 'hands') continue;
    const url = take(id);
    if (url) covers.set(id, url);
  }
  const hands = take('hands');
  if (hands) covers.set('hands', hands);
  return covers;
}

export function resolvePlaneSources(data: InvitationData): PlaneSource[] {
  const maps = photoMaps(data);
  const covers = claimCovers(readEvents(data));
  const gallery = Array.isArray(data.mediaUrls) ? data.mediaUrls : [];

  return PLOT_SCENES.map((scene, index) => {
    const id = scene.id;
    const fallbackSrc = assetUrl(scene.asset);
    const host = explicitUrl(maps, id) ?? covers.get(id) ?? hostPlaneUrl(gallery[index]);
    if (!host || host === fallbackSrc) {
      return { id, src: fallbackSrc, fallbackSrc, fromHost: false };
    }
    return { id, src: host, fallbackSrc, fromHost: true };
  });
}

export function planePhotosFromSearch(search: string): Partial<Record<PlaneSlotId, string>> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const photos: Partial<Record<PlaneSlotId, string>> = {};
  for (const id of PLANE_SLOT_IDS) {
    const value = params.get(id);
    if (value && value.trim()) photos[id] = value.trim();
  }
  return photos;
}

/** Demo/dev only: query keys become `customData.plotPlane.photos`. */
export function withDemoPlanePhotos(data: InvitationData, search: string): InvitationData {
  const photos = planePhotosFromSearch(search);
  if (Object.keys(photos).length === 0) return data;
  const custom = asRecord(data.customData) ?? {};
  return {
    ...data,
    customData: {
      ...custom,
      plotPlane: { photos },
    },
  };
}
